import React, { useState, useRef, useEffect } from 'react';
import { Award } from 'lucide-react';
import AddCertificationModal from '../components/AddCertificationModal';
import ConfirmDeleteModal from '../../../organisms/ConfirmDeleteModal';
import Toast from '../../../atoms/Toast';
import { API_ENDPOINTS } from '../../../../config';
import { api } from '../../../../utils/api';
import '../../../../styles/profile/certifications-tab.css';

interface CertificationData {
  uid?: string;
  certificationName: string;
  organization: string;
  issuedAt: string;
  expiresAt: string;
  credentialId: string;
  description: string;
}

interface Certification {
  uid: string;
  certification_name: string;
  organization_name: string | null;
  issued_at: string;
  expires_at: string | null;
  credential_id: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface UserData {
  certifications: Certification[] | null;
}

interface CertificationsTabProps {
  userData: UserData | null;
  certifications: Certification[];
}

interface CertificationCardProps {
  certification: Certification;
  onEdit: (certification: Certification) => void;
  onDelete: (uid: string) => void;
}

function CertificationCard({ certification, onEdit, onDelete }: CertificationCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const issuedDate = formatDate(certification.issued_at);
  const expiresDate = certification.expires_at ? formatDate(certification.expires_at) : 'No Expiration';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  return (
    <div className="certification-card">
      <div className="certification-card-header">
        <div className="certification-card-logo">
          <Award size={18} />
        </div>
        <div className="certification-card-info">
          <h3 className="certification-card-title">{certification.certification_name}</h3>
          {certification.organization_name && (
            <p className="certification-card-subtitle">{certification.organization_name}</p>
          )}
        </div>
        <p className="certification-card-issued">Issued: {issuedDate}</p>
        <p className="certification-card-expires">Expires: {expiresDate}</p>
        <div className="certification-card-menu-container" ref={menuRef}>
          <button 
            className="certification-card-menu" 
            type="button"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <div className="certification-card-dropdown">
              <button
                className="certification-card-dropdown-item"
                onClick={() => {
                  onEdit(certification);
                  setMenuOpen(false);
                }}
              >
                Edit certification
              </button>
              <button
                className="certification-card-dropdown-item certification-card-dropdown-item--delete"
                onClick={() => {
                  onDelete(certification.uid);
                  setMenuOpen(false);
                }}
              >
                Delete certification
              </button>
            </div>
          )}
        </div>
      </div>

      {(certification.credential_id || certification.description) && (
        <div className="certification-card-body">
          {certification.credential_id && (
            <p className="certification-card-credential">
              <span className="certification-card-credential-label">Credential ID:</span> {certification.credential_id}
            </p>
          )}
          {certification.description && (
            <p className="certification-card-description">{certification.description}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function CertificationsTab({ userData, certifications }: CertificationsTabProps) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [userCertifications, setUserCertifications] = useState<Certification[]>([]);
  const [selectedCertification, setSelectedCertification] = useState<Certification | null>(null);
  const [deleteUid, setDeleteUid] = useState<string | null>(null);

  useEffect(() => {
    if (certifications && certifications.length > 0) {
      setUserCertifications(certifications);
    }
  }, [certifications]);

  const handleImport = () => {
  };

  const handleAdd = () => {
    setSelectedCertification(null);
    setAddModalOpen(true);
  };

  const handleEdit = (certification: Certification) => {
    setSelectedCertification(certification);
    setEditModalOpen(true);
  };

  const handleDeleteClick = (uid: string) => {
    setDeleteUid(uid);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUid) return;

    setIsDeleting(true);

    try {
      
      await api.delete(API_ENDPOINTS.users.deleteCertifications(deleteUid));
      
      setUserCertifications(userCertifications.filter(cert => cert.uid !== deleteUid));
      setToast({ message: 'Certification deleted successfully! ✓', type: 'success' });
      setDeleteModalOpen(false);
      setDeleteUid(null);
    } catch (error) {
      setToast({ message: 'Failed to delete certification. Please try again.', type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddCertification = async (data: CertificationData) => {
    setIsCreating(true);

    try {
      const payload: any = {
        certification_name: data.certificationName,
      };

      if (data.uid) {
        payload.uid = data.uid;
      }

      if (data.organization.trim()) payload.organization_name = data.organization.trim();
      if (data.issuedAt) payload.issued_at = data.issuedAt;
      if (data.expiresAt) payload.expires_at = data.expiresAt;
      if (data.credentialId.trim()) payload.credential_id = data.credentialId.trim();
      if (data.description.trim()) payload.description = data.description.trim();

      const isEditMode = !!data.uid;

      const response = await api.post(API_ENDPOINTS.users.updateCertifications, payload);
      

      if (isEditMode) {
        setUserCertifications(userCertifications.map(cert => 
          cert.uid === data.uid ? { ...cert, ...response } : cert
        ));
        setToast({ message: 'Certification updated successfully! ✓', type: 'success' });
        setEditModalOpen(false);
        setSelectedCertification(null);
      } else {
        if (response && response.uid) {
          setUserCertifications([...userCertifications, response]);
        }
        setToast({ message: 'Certification added successfully! ✓', type: 'success' });
        setAddModalOpen(false);
      }
    } catch (error) {
      setToast({ message: 'Failed to save certification. Please try again.', type: 'error' });
    } finally {
      setIsCreating(false);
    }
  };

  const certificationToModalData = (cert: Certification | null): any => {
    if (!cert) return null;
    return {
      uid: cert.uid,
      certification_name: cert.certification_name,
      organization_name: cert.organization_name,
      issued_at: cert.issued_at,
      expires_at: cert.expires_at,
      credential_id: cert.credential_id,
      description: cert.description,
    };
  };

  return (
    <>
      <div className="certifications-tab">
        <div className="certifications-tab-header">
          <h1 className="certifications-tab-title">Certifications</h1>
          <button className="certifications-tab-btn certifications-tab-btn--import" onClick={handleImport} type="button">
            <span className="certifications-tab-btn-icon">⬇</span>
            Import
          </button>
        </div>

        <div className="certifications-tab-content">
          <button 
            type="button" 
            className="certifications-tab-add-btn"
            onClick={handleAdd}
          >
            <span className="certifications-tab-add-icon">+</span>
          </button>

          {userCertifications.length > 0 && (
            <div className="certifications-list">
              {userCertifications.map((cert) => (
                <CertificationCard 
                  key={cert.uid} 
                  certification={cert}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddCertificationModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddCertification}
      />

      <AddCertificationModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedCertification(null);
        }}
        onAdd={handleAddCertification}
        title="Edit Certification"
        initialData={certificationToModalData(selectedCertification)}
        isEditMode={true}
      />

      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        itemName={userCertifications.find(cert => cert.uid === deleteUid)?.certification_name || 'this certification'}
        itemType="Certification"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteUid(null);
        }}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
}
